import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Linkedin, MessageCircle } from 'lucide-react';

const Footer = ({ onContactClick, onHowItWorksClick, onSignupClick }) => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    if (onContactClick) {
      onContactClick();
    } else {
      // Navigate to home page contact section
      navigate('/', { state: { scrollTo: 'contact' } });
    }
  };

  const handleHowItWorksClick = () => {
    if (onHowItWorksClick) {
      onHowItWorksClick();
    } else {
      // Navigate to home page how it works section
      navigate('/', { state: { scrollTo: 'how-it-works' } });
    }
  };

  const handleSignupClick = () => {
    if (onSignupClick) {
      onSignupClick();
    } else {
      // Navigate to therapist signup page
      navigate('/therapist-signup');
    }
  };

  return (
    <footer className="bg-gray-800 dark:bg-dark-bg-primary text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-8">
          {/* Column 1: TheraP Track */}
          <div>
            <h4 className="font-bold text-lg mb-4">TheraP Track</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="text-gray-200 hover:text-white transition">
                  About us
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-200 hover:text-white transition">
                  Our Services
                </Link>
              </li>
              <li>
                <Link to="/team" className="text-gray-200 hover:text-white transition">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-200 hover:text-white transition">
                  Careers
                </Link>
              </li>
              <li>
                <button onClick={handleContactClick} className="text-gray-200 hover:text-white transition">
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Column 2: For Clients */}
          <div>
            <h4 className="font-bold text-lg mb-4">For Clients</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/therapists" className="text-gray-200 hover:text-white transition">
                  Search for Therapists
                </Link>
              </li>
              <li>
                <Link to="/therapists" className="text-gray-200 hover:text-white transition">
                  Search for Clinics
                </Link>
              </li>
              <li>
                <Link to="/therapists?type=couples" className="text-gray-200 hover:text-white transition">
                  Search for Couple Therapist
                </Link>
              </li>
              <li>
                <Link to="/therapists?type=child" className="text-gray-200 hover:text-white transition">
                  Search for Child Therapist
                </Link>
              </li>
              <li>
                <button onClick={handleContactClick} className="text-gray-200 hover:text-white transition">
                  Contact for Pro-Bono Therapy
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Social Media */}
          <div>
            <h4 className="font-bold text-lg mb-4 invisible">Social</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition">
                  <Facebook className="h-4 w-4" />
                  <span>Facebook</span>
                </a>
              </li>
              <li>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition">
                  <Instagram className="h-4 w-4" />
                  <span>Instagram</span>
                </a>
              </li>
              <li>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition">
                  <Youtube className="h-4 w-4" />
                  <span>YouTube</span>
                </a>
              </li>
              <li>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition">
                  <Linkedin className="h-4 w-4" />
                  <span>LinkedIn</span>
                </a>
              </li>
              <li>
                <a href="https://threads.net" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition">
                  <MessageCircle className="h-4 w-4" />
                  <span>Threads</span>
                </a>
              </li>
              <li>
                <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition">
                  <span className="text-lg">📌</span>
                  <span>Pinterest</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: For Therapists */}
          <div>
            <h4 className="font-bold text-lg mb-4">For Therapists</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <button onClick={handleHowItWorksClick} className="text-gray-200 hover:text-white transition">
                  How to Use
                </button>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-200 hover:text-white transition">
                  Pricing
                </Link>
              </li>
              <li>
                <button onClick={handleSignupClick} className="text-gray-200 hover:text-white transition">
                  Create Therapist Account
                </button>
              </li>
              <li>
                <Link to="/login" className="text-gray-200 hover:text-white transition">
                  Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 5: For Corporates • For Institutes */}
          <div>
            <h4 className="font-bold text-lg mb-4">For Corporates • For Institutes</h4>
            <ul className="space-y-2 text-sm">
              <li className="text-gray-200">
                {/* Placeholder for future content */}
              </li>
            </ul>
          </div>

          {/* Column 6: More Details */}
          <div>
            <h4 className="font-bold text-lg mb-4">More Details</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy-policy" className="text-gray-200 hover:text-white transition">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className="text-gray-200 hover:text-white transition">
                  Terms and Conditions
                </Link>
              </li>
              <li>
                <button onClick={handleContactClick} className="text-gray-200 hover:text-white transition">
                  Book Demo for Therapeutic Organizations
                </button>
              </li>
              <li>
                <Link to="/faq" className="text-gray-200 hover:text-white transition">
                  FAQ
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8 text-center text-sm text-gray-200">
          <p>&copy; 2026 TheraP Track. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
